import { ProfileEntryComposer, type ProfileEntrySaveError } from '@/components/ProfileEntryComposer'
import { logger } from '@jobilla/instrumentation'
import {
	useCandidacyProfileEntryDestroy,
	useCandidacyProfileEntryStore,
	useCandidacyProfileEntryUpdate,
} from '@jobilla/nexus-sdk/lib/hooks/useCandidacyProfileEntry'
import type { CandidateProfileEntryData } from '@jobilla/nexus-sdk/lib/types/CandidateProfileEntryData'
import type { CandidateProfileSection } from '@jobilla/nexus-sdk/lib/types/CandidateProfileSection'
import { maxProfileEntriesPerSection } from '@jobilla/nexus-sdk/lib/types/MaxProfileEntriesPerSection'
import { profileEntryLimitReached } from '@jobilla/nexus-sdk/lib/types/ProfileEntryLimitReached'
import { useBlocker, useNavigate } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

type OpenComposer = { kind: 'new' } | { kind: 'edit'; entryId: string } | null

type ProfileEntryAction = 'add' | 'edit' | 'remove'

const sectionTracking = {
	work_history: {
		savedEventNames: {
			add: 'Candidate Center: Added work history',
			edit: 'Candidate Center: Edited work history',
			remove: 'Candidate Center: Removed work history',
		},
		failedEventName: 'Candidate Center: Work history save failed',
		countProperty: 'work_history_count',
	},
	education: {
		savedEventNames: {
			add: 'Candidate Center: Added education',
			edit: 'Candidate Center: Edited education',
			remove: 'Candidate Center: Removed education',
		},
		failedEventName: 'Candidate Center: Education save failed',
		countProperty: 'education_count',
	},
	licenses: {
		savedEventNames: {
			add: 'Candidate Center: Added license',
			edit: 'Candidate Center: Edited license',
			remove: 'Candidate Center: Removed license',
		},
		failedEventName: 'Candidate Center: License save failed',
		countProperty: 'licenses_count',
	},
} as const satisfies Record<CandidateProfileSection, unknown>

export function ProfileEntries(
	{ section, organisationId, candidacyId, entries, onChanged }: {
		section: CandidateProfileSection
		organisationId: string
		candidacyId: string
		entries: CandidateProfileEntryData[]
		onChanged: () => void
	},
) {
	const { t } = useTranslation('common')
	const navigate = useNavigate()
	const [openComposer, setOpenComposer] = useState<OpenComposer>(null)
	const [saveError, setSaveError] = useState<ProfileEntrySaveError | null>(null)
	const tracking = sectionTracking[section]
	const storeEntry = useCandidacyProfileEntryStore()
	const updateEntry = useCandidacyProfileEntryUpdate()
	const destroyEntry = useCandidacyProfileEntryDestroy()
	const isNewComposerOpen = entries.length === 0 || openComposer?.kind === 'new'
	const isAnyComposerOpen = isNewComposerOpen || openComposer?.kind === 'edit'
	const canAddEntry = entries.length < maxProfileEntriesPerSection
	const isAddButtonVisible = !isNewComposerOpen && canAddEntry
	const showTimeline = isAddButtonVisible || entries.length + (isNewComposerOpen ? 1 : 0) > 1
	const hasUnsavedChangesRef = useRef(false)

	const trackUnsavedChanges = (hasUnsavedChanges: boolean) => {
		hasUnsavedChangesRef.current = hasUnsavedChanges
	}

	const confirmDiscardingChanges = () =>
		!hasUnsavedChangesRef.current || window.confirm(t(`profile.${section}.unsaved_changes`))

	useBlocker({
		shouldBlockFn: () => !confirmDiscardingChanges(),
		enableBeforeUnload: () => hasUnsavedChangesRef.current,
	})

	const openComposerFor = (composer: OpenComposer) => {
		setSaveError(null)
		setOpenComposer(composer)
	}

	const switchComposerTo = (composer: OpenComposer) => {
		if (confirmDiscardingChanges()) {
			openComposerFor(composer)
		}
	}

	const entryCountsAfter: Record<ProfileEntryAction, number> = {
		add: entries.length + 1,
		edit: entries.length,
		remove: entries.length - 1,
	}

	const handleSuccess = (action: ProfileEntryAction) => {
		logger.track(tracking.savedEventNames[action], {
			data: { [tracking.countProperty]: entryCountsAfter[action], candidate_id: candidacyId },
		})
		trackUnsavedChanges(false)
		openComposerFor(null)
		onChanged()
	}

	const handleFailure = (action: ProfileEntryAction, status: number | null, isLimitReached = false) => {
		logger.track(tracking.failedEventName, { data: { action, status, candidate_id: candidacyId } })

		if (status === 401) {
			trackUnsavedChanges(false)
			void navigate({ to: '/', replace: true })
			return
		}

		setSaveError(isLimitReached ? 'limit' : 'failed')
	}

	return (
		<div className="flex flex-col gap-2">
			<h2 className="text-md font-semibold text-grey-900">{t(`profile.${section}.title`)}</h2>
			<p className="text-sm text-grey-500">{t(`profile.${section}.description`)}</p>

			<ol className="mt-4 flex flex-col">
				{entries.map((entry, entryIndex) => (
					<li key={entry.id} className="flex gap-3.5">
						{showTimeline
							? (
								<TimelineMarker
									variant="saved"
									isLast={entryIndex === entries.length - 1 && !isNewComposerOpen
										&& !isAddButtonVisible}
								/>
							)
							: null}
						<div className="flex-1 pb-5">
							{openComposer?.kind === 'edit' && openComposer.entryId === entry.id
								? (
									<ProfileEntryComposer
										section={section}
										initialContents={entry.contents}
										onUnsavedChange={trackUnsavedChanges}
										isSaving={updateEntry.isPending}
										saveError={saveError}
										onCancel={() => openComposerFor(null)}
										onSave={contents =>
											updateEntry.mutate({
												organisation: organisationId,
												candidacy: candidacyId,
												section,
												entry: entry.id,
												data: { contents },
											}, {
												onSuccess: () => handleSuccess('edit'),
												onError: error => handleFailure('edit', error.response?.status ?? null),
											})}
									/>
								)
								: (
									<>
										<p className="whitespace-pre-line text-sm leading-relaxed text-grey-900">
											{entry.contents}
										</p>
										<div className="-ml-2 flex gap-1">
											<button
												type="button"
												onClick={() => switchComposerTo({ kind: 'edit', entryId: entry.id })}
												className="min-h-11 cursor-pointer px-2 text-[13px] font-medium text-grey-500 hover:text-grey-700"
											>
												{t(`profile.${section}.edit`)}
											</button>
											<button
												type="button"
												disabled={destroyEntry.isPending}
												onClick={() =>
													destroyEntry.mutate({
														organisation: organisationId,
														candidacy: candidacyId,
														section,
														entry: entry.id,
													}, {
														onSuccess: () => handleSuccess('remove'),
														onError: error =>
															handleFailure('remove', error.response?.status ?? null),
													})}
												className="min-h-11 cursor-pointer px-2 text-[13px] font-medium text-grey-500 hover:text-grey-700 disabled:cursor-not-allowed"
											>
												{t(`profile.${section}.remove`)}
											</button>
										</div>
									</>
								)}
						</div>
					</li>
				))}
				{isNewComposerOpen
					? (
						<li className="flex gap-3.5">
							{showTimeline ? <TimelineMarker variant="draft" isLast={true} /> : null}
							<div className="flex-1 pb-4">
								<ProfileEntryComposer
									section={section}
									initialContents=""
									onUnsavedChange={trackUnsavedChanges}
									isSaving={storeEntry.isPending}
									saveError={saveError}
									onCancel={entries.length === 0 ? null : () => openComposerFor(null)}
									onSave={contents =>
										storeEntry.mutate({
											organisation: organisationId,
											candidacy: candidacyId,
											section,
											data: { contents },
										}, {
											onSuccess: () => handleSuccess('add'),
											onError: error => {
												const errorData = error.response?.data
												const isLimitReached = errorData !== undefined && 'errors' in errorData
													&& (errorData.errors.contents ?? []).includes(
														profileEntryLimitReached,
													)

												handleFailure('add', error.response?.status ?? null, isLimitReached)
											},
										})}
								/>
							</div>
						</li>
					)
					: null}
				{isAddButtonVisible
					? (
						<li className="flex gap-3.5">
							<div aria-hidden={true} className="flex w-[13px] flex-col items-center">
								<span className="w-px flex-1 bg-grey-200" />
								<span className="flex size-[13px] shrink-0 items-center justify-center rounded-full border border-dashed border-grey-400 bg-white text-grey-500">
									<svg
										viewBox="0 0 7 7"
										fill="none"
										stroke="currentColor"
										strokeWidth={1}
										strokeLinecap="round"
										className="size-[7px]"
									>
										<path d="M3.5 0.5v6M0.5 3.5h6" />
									</svg>
								</span>
								<span className="flex-1" />
							</div>
							<button
								type="button"
								onClick={() => switchComposerTo({ kind: 'new' })}
								className="min-h-11 cursor-pointer text-sm font-medium text-brand-600 hover:text-brand-700"
							>
								{t(`profile.${section}.add_another`)}
							</button>
						</li>
					)
					: null}
			</ol>

			{saveError !== null && !isAnyComposerOpen
				? <p role="alert" className="text-sm text-red-600">{t('login.request_error.generic')}</p>
				: null}
		</div>
	)
}

function TimelineMarker({ variant, isLast }: { variant: 'saved' | 'draft'; isLast: boolean }) {
	return (
		<div aria-hidden={true} className="flex w-[13px] flex-col items-center">
			{variant === 'saved'
				? <span className="mt-1.5 size-[11px] shrink-0 rounded-full bg-brand-600" />
				: (
					<>
						<span className="h-3.5 w-px shrink-0 bg-grey-200" />
						<span className="size-[13px] shrink-0 rounded-full border-[1.5px] border-brand-600 bg-white" />
					</>
				)}
			{isLast ? null : <span className="w-px flex-1 bg-grey-200" />}
		</div>
	)
}
